import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import * as Sentry from "@sentry/react"
import { MantineProvider } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/charts/styles.css'
import '@mantine/tiptap/styles.css'
import './index.css'
import App from './App.jsx'
import ErrorFallback from './components/ErrorFallback'

// Initialize Sentry for production and staging environments
if (import.meta.env.VITE_ENABLE_SENTRY === 'true') {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENV || import.meta.env.MODE,

    // Integrations
    integrations: [
      // Performance monitoring
      Sentry.browserTracingIntegration({
        // Track navigation and interactions
        tracePropagationTargets: ["localhost", /^\//],
      }),

      // Session replay for debugging
      Sentry.replayIntegration({
        // Mask sensitive data
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],

    // Performance monitoring sample rate
    // 20% in production = good balance of data vs cost
    tracesSampleRate: import.meta.env.VITE_APP_ENV === 'production' ? 0.2 : 1.0,

    // Session Replay sample rates
    // Capture 10% of normal sessions for performance insights
    replaysSessionSampleRate: 0.1,
    // Capture 100% of sessions with errors
    replaysOnErrorSampleRate: 1.0,

    // Release tracking (set during build)
    release: import.meta.env.VITE_APP_VERSION || '1.0.0',

    // Error filtering
    beforeSend(event, hint) {
      // Don't send errors from browser extensions
      if (event.exception) {
        const error = hint.originalException
        if (error && error.message && error.message.match(/extension\//i)) {
          return null
        }
      }
      return event
    },

    // Ignore common non-actionable errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      'cancelled', // Navigation cancelled
    ],
  })

  console.log(`[Sentry] Initialized in ${import.meta.env.VITE_APP_ENV || 'development'} environment`)
}

createRoot(document.getElementById('root')).render(
  <StrictMode>
    <Sentry.ErrorBoundary fallback={ErrorFallback} showDialog>
      <MantineProvider defaultColorScheme="light">
        <ModalsProvider>
          <Notifications position="top-right" />
          <App />
        </ModalsProvider>
      </MantineProvider>
    </Sentry.ErrorBoundary>
  </StrictMode>,
)
